import { BigNumber } from 'bignumber.js';
import type { JsonRpcSigner } from 'ethers';
import {
  type BuildingsRegistry,
  BuildingsRegistry__factory,
  type BuildingsRent,
  BuildingsRent__factory,
  ERC721__factory,
  Multicall__factory
} from 'public/abi/evm/types';
import type {
  IHouseFixResource,
  IMarketHouseInterface,
  IUserHouseInterface,
  RentContractInitParams,
  RentEventContractInterface
} from '~/stores/rent-event-store/rent-contract/rent-contract.interface';
import { FilterType } from '~/stores/rent-event-store/types';
import { modifyHistoryHashStatus, saveHashToLocalStorage } from '~/utils';
import { addLog } from '~/utils/prepare-win-logger';

export class RentContractImpl implements RentEventContractInterface {
  readonly buildingsRegistryRead: BuildingsRegistry;
  readonly buildingsRentRead: BuildingsRent;

  buildingsRegistryWrite: BuildingsRegistry | null = null;
  buildingsRentWrite: BuildingsRent | null = null;

  constructor(private params: RentContractInitParams) {
    this.stakeHouses = this.stakeHouses.bind(this);
    this.unstakeHouses = this.unstakeHouses.bind(this);
    this.fixHouses = this.fixHouses.bind(this);
    this.claimUserEarnings = this.claimUserEarnings.bind(this);
    this.setupSigner = this.setupSigner.bind(this);

    this.buildingsRegistryRead = BuildingsRegistry__factory.connect(
      params.blockchain.contracts.buildingsRegistry,
      params.provider
    );
    this.buildingsRentRead = BuildingsRent__factory.connect(params.blockchain.contracts.buildingsRent, params.provider);

    addLog('prepareSigner');
    void this.setupSigner(params.signer);
  }

  public async setupSigner(signer: Promise<JsonRpcSigner>) {
    await signer
      .catch((e: any) => {
        addLog('signerFailed', e?.message);
      })
      .then((signer) => {
        if (!signer) {
          addLog('signerFailed', 'signer is null', signer);
          return;
        }
        this.buildingsRegistryWrite = BuildingsRegistry__factory.connect(
          this.params.blockchain.contracts.buildingsRegistry,
          signer
        );
        this.buildingsRentWrite = BuildingsRent__factory.connect(
          this.params.blockchain.contracts.buildingsRent,
          signer
        );
      });
  }

  public async getTotalEarnedValue() {
    const val = await this.buildingsRentRead.totalEarned();

    return new BigNumber(val.toString()).shiftedBy(-18).dp(4);
  }

  public async getUserEarnings(userAddress: string) {
    const val = await this.buildingsRentRead.getUserEarned(userAddress);
    return new BigNumber(val.toString()).shiftedBy(-18).dp(4);
  }

  public async getUserSharePercent(userAddress: string) {
    const val = await this.buildingsRentRead.getUserShare(userAddress);
    return new BigNumber(val.toString()).shiftedBy(-16).dp(4);
  }

  public async getUserHouses(userAddress: string) {
    const nftAddresses = await this.getAvailableNftAddresses();
    const mc = Multicall__factory.connect(this.params.blockchain.multicallAddress, this.params.provider);

    const resulting = await Promise.all(
      nftAddresses.map(async (nftAddress) => {
        const erc721Contract = ERC721__factory.connect(nftAddress, this.params.provider);

        const userTokens = await erc721Contract.getTokenIds(userAddress);
        const type = (() => {
          const addr = nftAddress.toLowerCase();
          if (this.params.blockchain.contracts.woodenHuts.addresses.contract.toLowerCase() === addr)
            return FilterType.Wooden;
          if (this.params.blockchain.contracts.tiledBrickHuts?.addresses?.contract?.toLowerCase() === addr)
            return FilterType.TiledBrick;
          return FilterType.Brick;
        })();

        const [metadata, durability, rentOutState, timestamps, earnedRaw, profitsRaw] = await Promise.all([
          mc.tryAggregate.staticCall(
            true,
            userTokens.map((tokenId) => ({
              target: nftAddress,
              callData: erc721Contract.interface.encodeFunctionData('getMetadata', [tokenId])
            }))
          ),
          mc.tryAggregate.staticCall(
            true,
            userTokens.map((tokenId) => ({
              target: this.params.blockchain.contracts.buildingsRegistry,
              callData: this.buildingsRegistryRead.interface.encodeFunctionData(
                'getBuildingDurability(address,uint256)',
                [nftAddress, tokenId]
              )
            }))
          ),
          mc.tryAggregate.staticCall(
            true,
            userTokens.map((tokenId) => ({
              target: this.params.blockchain.contracts.buildingsRegistry,
              callData: this.buildingsRegistryRead.interface.encodeFunctionData(
                'isBuildingRegistered(address,uint256)',
                [nftAddress, tokenId]
              )
            }))
          ),
          mc.tryAggregate.staticCall(
            true,
            userTokens.map((tokenId) => ({
              target: this.params.blockchain.contracts.buildingsRent,
              callData: this.buildingsRentRead.interface.encodeFunctionData('getTokenTimestamps', [nftAddress, tokenId])
            }))
          ),
          mc.tryAggregate.staticCall(
            true,
            userTokens.map((tokenId) => ({
              target: this.params.blockchain.contracts.buildingsRent,
              callData: this.buildingsRentRead.interface.encodeFunctionData('getTokenEarned', [nftAddress, tokenId])
            }))
          ),
          mc.tryAggregate.staticCall(
            true,
            userTokens.map((tokenId) => ({
              target: this.params.blockchain.contracts.buildingsRent,
              callData: this.buildingsRentRead.interface.encodeFunctionData('getProfitPerPeriodForToken', [
                nftAddress,
                tokenId
              ])
            }))
          )
        ]);

        const houseTypes = metadata.map((m) => {
          const x = erc721Contract.interface.decodeFunctionResult('getMetadata', m[1]);
          return parseInt(x[0][0]);
        });

        const housesDurability = durability.map((m) => {
          const x = this.buildingsRegistryRead.interface.decodeFunctionResult(
            'getBuildingDurability(address,uint256)',
            m[1]
          );

          return new BigNumber(x[0]?.toString() || '0').shiftedBy(-16).dp(2, BigNumber.ROUND_DOWN).toNumber();
        });

        const rentOutStates = rentOutState.map((m) => {
          return m[1].slice(-1) === '1';
        });

        const endTimestamps = timestamps.map((m) => {
          const x = this.buildingsRentRead.interface.decodeFunctionResult('getTokenTimestamps', m[1]);

          return new Date((x[1]?.toString() || '0') * 1000);
        });

        const earned = earnedRaw.map((m) => {
          const decoded = this.buildingsRentRead.interface.decodeFunctionResult('getTokenEarned', m[1]);
          return new BigNumber(decoded[0]?.toString() || '0').shiftedBy(-18).dp(5).toNumber();
        });

        const houseProfits = profitsRaw.map((m) => {
          const decoded = this.buildingsRentRead.interface.decodeFunctionResult('getProfitPerPeriodForToken', m[1]);
          return new BigNumber(decoded[0]?.toString() || '0').shiftedBy(-18).dp(2).toNumber();
        });

        const iconExt = type === FilterType.TiledBrick ? 'png' : 'jpg';
        const res: IUserHouseInterface[] = userTokens.map((tokenId, i) => ({
          address: nftAddress,
          type,
          tokenId: tokenId.toString(),
          buildingType: houseTypes[i].toFixed(),
          durability: housesDurability[i] ?? 100,
          rentedOut: rentOutStates[i] ?? false,
          nameKey: `rentBuilding_${type}Hut${houseTypes[i] + 1}`,
          earned: earned[i] ?? 0,
          icon: `/img/tokens/${type}Hut${houseTypes[i] + 1}.${iconExt}`,
          endTimestamp: endTimestamps[i] ?? new Date(0),
          profit: houseProfits[i] ?? 0
        }));

        return res;
      })
    );

    return resulting.flat();
  }

  public async getMarketHouses(userAddress?: string) {
    const nftAddresses = await this.getAvailableNftAddresses();

    const resulting = await Promise.all(
      nftAddresses.map(async (nftAddress) => {
        const [totalRentedOut, userRentedOut, buildingPrice, finalProfit] = await Promise.all([
          this.buildingsRentRead.typesCount(nftAddress),
          userAddress ? await this.buildingsRentRead.userBuildingsCount(userAddress, nftAddress) : Promise.resolve([]),
          await this.buildingsRentRead.typesValue(nftAddress),
          await this.buildingsRentRead.getFinalProfitsPerBuilding(nftAddress)
        ]);

        const type = (() => {
          const addr = nftAddress.toLowerCase();
          if (this.params.blockchain.contracts.woodenHuts.addresses.contract.toLowerCase() === addr)
            return FilterType.Wooden;
          if (this.params.blockchain.contracts.tiledBrickHuts?.addresses?.contract?.toLowerCase() === addr)
            return FilterType.TiledBrick;
          return FilterType.Brick;
        })();

        const iconExt = type === FilterType.TiledBrick ? 'png' : 'jpg';
        const res: IMarketHouseInterface[] = Array(5)
          .fill({})
          .map((_, i) => ({
            buildPrice: new BigNumber(buildingPrice[i]?.toString() || '0').shiftedBy(-2).dp(2).toNumber(),
            nftAddress,
            type,
            buildingType: i.toFixed(),
            finalProfitPerYear: new BigNumber(finalProfit[i]?.toString() || '0')
              .shiftedBy(-18)
              .multipliedBy(0.9)
              .dp(2)
              .toFixed(),
            totalRentedOut: new BigNumber(totalRentedOut[i]?.toString() || '0').dp(0).toNumber(),
            rentedOutByUser: new BigNumber(userRentedOut[i]?.toString() || '0').dp(0).toNumber(),
            nameKey: `rentBuilding_${type}Hut${i + 1}`,
            icon: `/img/tokens/${type}Hut${i + 1}.${iconExt}`
          }));

        return res;
      })
    );

    return resulting.flat();
  }

  public async stakeHouses(houses: IUserHouseInterface[]) {
    if (!this.buildingsRentWrite) throw new Error('Write contract not initialized');
    const callData = [...houses].map((house) => {
      return this.buildingsRentRead.interface.encodeFunctionData('rentBatch', [house.address, [house.tokenId]]);
    });

    const tx = await this.buildingsRentWrite.multicall(callData);

    if (!tx) return false;

    saveHashToLocalStorage(`${this.params.t('rentTxStake')}`, tx.hash);
    this.params.updateStoreVersion();

    await tx.wait(1);

    modifyHistoryHashStatus(tx.hash, 'Done');
    this.params.updateStoreVersion();

    return true;
  }

  public async unstakeHouses(houses: IUserHouseInterface[]) {
    if (!this.buildingsRentWrite) throw new Error('Write contract not initialized');
    const callData = [...houses].map((house) => {
      return this.buildingsRentRead.interface.encodeFunctionData('stopRent', [house.address, house.tokenId]);
    });

    const tx = await this.buildingsRentWrite.multicall(callData);

    if (!tx) return false;

    saveHashToLocalStorage(`${this.params.t('rentTxUnstake')}`, tx.hash);
    this.params.updateStoreVersion();

    await tx.wait(1);

    modifyHistoryHashStatus(tx.hash, 'Done');
    this.params.updateStoreVersion();

    return true;
  }

  public async fixHouses(houses: IUserHouseInterface[]) {
    if (!this.buildingsRegistryWrite) throw new Error('Write contract not initialized');
    const callData = houses.map((house) => {
      return this.buildingsRegistryRead.interface.encodeFunctionData('fixBuilding', [house.address, house.tokenId]);
    });

    const tx = await this.buildingsRegistryWrite.multicall(callData);

    if (!tx) return false;

    saveHashToLocalStorage(`${this.params.t('rentTxRepair')}`, tx.hash);
    this.params.updateStoreVersion();

    await tx.wait(1);

    modifyHistoryHashStatus(tx.hash, 'Done');
    this.params.updateStoreVersion();

    return true;
  }

  public async getHouseFixResources(houses: IUserHouseInterface[]) {
    const contract = Multicall__factory.connect(this.params.blockchain.multicallAddress, this.params.provider);

    const result = await contract.tryAggregate.staticCall(
      true,
      houses.map((i) => ({
        target: this.params.blockchain.contracts.buildingsRegistry,
        callData: this.buildingsRegistryRead.interface.encodeFunctionData('getResourcesForFixBuilding', [
          i.address,
          i.tokenId
        ])
      }))
    );

    const resources = result.map((r, i) => {
      const decoded = this.buildingsRegistryRead.interface.decodeFunctionResult('getResourcesForFixBuilding', r[1]);

      const tokensWithQuantity = [
        ...decoded[0].map((i: any) => ({
          item: i[0],
          quantity: new BigNumber(i[1]?.toString() || '0').shiftedBy(-18).dp(7).toNumber()
        }))
      ];

      const fixResources: IHouseFixResource[] = tokensWithQuantity.map((t) => ({
        quantity: t.quantity,
        tokenAddress: t.item,
        nftAddress: houses[i].address,
        buildingType: houses[i].buildingType
      }));

      return fixResources;
    });

    return resources.flat();
  }

  public async getHouseFixSeasonPoints(resources: IHouseFixResource[]) {
    if (!this.buildingsRegistryRead) throw new Error('Write contract not initialized');

    const value = await this.buildingsRegistryRead.getSeasonPoints(
      resources.map((i) => ({
        token: i.tokenAddress,
        quantity: new BigNumber(i.quantity).shiftedBy(18).toFixed(0)
      }))
    );

    return value.toString();
  }

  public async getAvailableNftAddresses() {
    return this.buildingsRentRead.getNftContracts();
  }

  public async claimUserEarnings(): Promise<boolean> {
    if (!this.buildingsRentWrite) throw new Error('Write contract not initialized');

    const tx = await this.params.sendContractMethod({
      contract: this.buildingsRentWrite,
      methodName: 'claimAllRewards',
      args: []
    });

    if (!tx) return false;

    saveHashToLocalStorage(`${this.params.t('rentTxClaim')}`, tx.hash);
    this.params.updateStoreVersion();

    await tx.wait(1);

    modifyHistoryHashStatus(tx.hash, 'Done');
    this.params.updateStoreVersion();

    return true;
  }
}
